import { DropdownProps } from "components/dropdowns/models";
import BaseReactSelectDropdown from "components/dropdowns/reactselect/BaseReactSelectDropdown";
import React from "react";
import { type GroupBase } from "react-select";
import { formatDeadline, getTimeBeforeShowLabel } from "userTask/beforeShowLabelHelper";

interface DeadlineOption {
  value: number
  label: string
};

export interface DeadlineTemplateOption {
  days: number
  type: server.dto.UserTaskDeadlineTypes
  label: string
}

const preciseDateOption = { value: undefined, label: RESX.DateTime.PreciseDate };

const generateBeforeAfterOptions = () => {
  const beforeOptions: DeadlineOption[] = [];
  // beforeOptions.push({ value: 0, label: RESX.DateTime.PreciseDate });

  for (let i = 0; i < 3; i++) {
    const days = i + 1;
    beforeOptions.push({
      value: -days,
      label: getTimeBeforeShowLabel(-days)
    });
  }

  for (let i = 3; i < 13; i++) {
    const days = i + 1;
    beforeOptions.push({
      value: -days,
      label: getTimeBeforeShowLabel(-days)
    });
  }

  for (let i = 1; i < 20; i++) {
    const weeks = i + 1;
    const days = weeks * 7;
    beforeOptions.push({
      value: -days,
      label: getTimeBeforeShowLabel(-days)
    });
  }

  const afterOptions = beforeOptions.map<DeadlineOption>(o => ({ value: -o.value, label: getTimeBeforeShowLabel(-o.value) }));
  return { beforeOptions, afterOptions };
}

const generateOptions = (): Array<GroupBase<DeadlineOption>> => {
  const { beforeOptions, afterOptions } = generateBeforeAfterOptions();

  return [
    {
      label: undefined,
      options: [preciseDateOption]
    },
    {
      label: RESX.DateTime.On,
      options: [{ value: 0, label: getTimeBeforeShowLabel(0) }]
    },
    {
      label: RESX.DateTime.Before,
      options: beforeOptions
    }, {
      label: RESX.DateTime.After,
      options: afterOptions
    }
  ];
};

const generateTaskTemplateOptions = (): Array<GroupBase<DeadlineTemplateOption>> => {
  const { beforeOptions, afterOptions } = generateBeforeAfterOptions();

  return [{
    label: RESX.DateTime.On,
    options: [{
      days: 0,
      type: "relativeToShowDate",
      label: getTimeBeforeShowLabel(0)
    }, {
      days: 0,
      type: "absolute",
      label: formatDeadline("absolute", 0)
    }]
  }, {
    label: RESX.DateTime.Before,
    options: beforeOptions.map(o => ({ days: o.value, type: "relativeToShowDate", label: o.label }))
  }, {
    label: RESX.DateTime.After,
    options: afterOptions.map(o => ({ days: o.value, type: "relativeToShowDate", label: o.label }))
  }];
}

const options = generateOptions();

interface Props {
  isRelativeDeadline: boolean
  value: number
  onChangeDays: (days: number) => void
  onIsNoLongerRelativeDeadline: () => void
}

export const DeadlineOptionsDropdown = (props: Props) => {
  const selectedValue = React.useMemo(() => {
    if (!props.isRelativeDeadline) return preciseDateOption;
    else return options.flatMap(o => o.options).find(o => o.value === props.value);
  }, [props.value, props.isRelativeDeadline]);

  return <BaseReactSelectDropdown<DeadlineOption>
    excludeKeys={undefined}
    getLabel={o => o.label}
    getValue={o => o.value}
    value={selectedValue}
    onChange={v => {
      if (v.value == null) props.onIsNoLongerRelativeDeadline();
      else props.onChangeDays(v.value);
    }}
    options={options}
    config={{
      isSearchable: false
    }}
  />;
};

const optionsTemplate = generateTaskTemplateOptions();

export const DeadlineOptionsDropdownForTemplate = (props: DropdownProps<DeadlineTemplateOption, string>) => {
  return <BaseReactSelectDropdown<DeadlineTemplateOption, false, string>
    excludeKeys={undefined}
    options={optionsTemplate}
    getLabel={o => o.label}
    getValue={o => `${o.days} ${o.type}`}
    value={props.value}
    onChange={v => props.onChange(v)}
    config={{
      ...props.config,
      isSearchable: false
    }}
  />;
}